/**
 * The 12-step view of one publish attempt (Registry page).
 *
 * User requirement 2026-10-14: an attempt shows ALL publish steps, always, in
 * a fixed logical order, each as a full row (state, start, end, duration,
 * where it ran, libc/platform, builds with job links, raw reason, details).
 * Pending steps are greyed, steps that were not run say why, steps switched
 * off by a per-package toggle read "off (operator)". The numbering is
 * LOGICAL; the timestamps are the true chronology (publish happens before
 * the benchmark finishes, the darwin build runs after publish).
 *
 *  1  Combined test build      runner VM    (musl standalone; today the
 *                                            package-test + full-project
 *                                            compile jobs as sub-rows)
 *  2  Package-test run         publish host (accuracy vs the node oracle)
 *  3  Full-project run         publish host
 *  4  Type-profile collection  runner VM    (addon, instrumented)
 *  5  Type-profile training    publish host
 *  6  LLVM instrumented build  runner VM    (addon, package crates only)
 *  7  LLVM training run        publish host (+ text-order derivation)
 *  8  Final addon build        runner VM    (which optimisations applied)
 *  9  Addon verify             publish host, glibc
 * 10  Publish                  publish host → registry
 *       10a package published to the registry
 * ---- from here on the pieces are INDEPENDENT of each other ----
 * 11  darwin arm64 follow-up   runner VM    (decoupled, after publish)
 *       11a macOS hardware verify
 * 12  Benchmark pages (/-/stats/<pkg>), FOUR components, each published
 *     when it settles and each with its own state:
 *       12a fixtures   (content-addressed, published first)
 *       12b glibc      12c musl
 *       12d macOS      (after 11 ships and 12a is up; hours later)
 *
 * The data is the publisher's `steps` checklist (internal/publish
 * stepChecklist.ts, D1 publish_versions.steps; migration 0083) plus the row's
 * native_targets and settings; this file only maps it onto the 13 steps.
 * Compile and run are separate raw facts today (package-test is one raw row
 * that spans its compile and its run: `runStartedAt` splits them), and the
 * "combined test build" collapses to the two existing compile jobs until
 * publish compiles once. A future raw `test-build` row is used as-is.
 * Old attempts (no `steps`) get no view: the page says "no step detail".
 */
import {
  STATE_LABEL,
  parseSteps,
  withDarwinFromTargets,
  type StepRecord,
  type StepState,
  type StepBuild,
} from "./stepChecklist.ts";
import { darwinRecordFor } from "./nativeTargets.ts";

export type { StepState };

export interface BuildRef {
  label: string;
  jobId: string | null;
  seqText: string | null;
  local: boolean;
  status: string;
}

export interface DetailLine {
  label: string;
  value: string;
}

export interface ViewLine {
  title: string;
  state: "ok" | "partial" | "unavailable";
  reason: string | null;
  headline: string | null;
}

export type StepKind = "runner-build" | "local-run" | "publish" | "benchmark";

export interface PublishStepRow {
  /** "1".."13", "13a", "13b", or "1.1" for a sub-row. */
  n: string;
  key: string;
  title: string;
  what: string;
  kind: StepKind;
  /** Who / where it ran. */
  where: string;
  /** libc / platform badges: "musl", "glibc", "macos-arm64", "linux-x64". */
  badges: string[];
  state: StepState;
  /** The pill text: the state, or "off (operator)" for a toggled-off step. */
  stateLabel: string;
  off: string | null;
  startedAt: number | null;
  endedAt: number | null;
  durationMs: number | null;
  /** Runner builds behind this row ("2 runner builds"). */
  runnerBuilds: number;
  builds: BuildRef[];
  subrows: PublishStepRow[];
  /** The raw reason of a degraded / failed / skipped step. */
  reason: string | null;
  details: DetailLine[];
  views: ViewLine[];
  /** "Node 26 · Alpine musl · view 5/6 · workload 4/12 (parse)" while running. */
  progress: string | null;
  /** Registry-relative path of a link this step produces (13b). */
  linkPath: string | null;
}

export interface PublishStepsView {
  rows: PublishStepRow[];
  /** First start to last end (now while anything runs), benchmark included. */
  elapsedMs: number | null;
  runnerBuilds: number;
  localRuns: number;
  /** "5 runner builds, 4 local runs". */
  summary: string;
  terminal: boolean;
}

export interface AttemptInput {
  status: string;
  steps: unknown;
  nativeTargets?: string | null;
  /** publish_versions.settings (JSON EffectiveSettings, migration 0085). */
  settings?: unknown;
  packageName: string;
  /** publish_versions.published_as */
  publishedAs?: string | null;
}

const TERMINAL = new Set([
  "published",
  "failed",
  "cancelled",
  "verified",
  "withdrawn",
  "superseded",
]);

// ── settings (toggles) ────────────────────────────────────────────────────
interface Toggle {
  on: boolean;
  label: string;
}
interface Settings {
  typeProfile?: Toggle;
  llvmPgo?: Toggle;
  textOrder?: Toggle;
  runsInstrumented?: boolean;
  orderOnly?: boolean;
}

function parseToggle(x: unknown): Toggle | undefined {
  if (!x || typeof x !== "object") return undefined;
  const t = x as Record<string, unknown>;
  if (typeof t["on"] !== "boolean") return undefined;
  return {
    on: t["on"],
    label: typeof t["label"] === "string" ? t["label"] : t["on"] ? "on" : "off",
  };
}

export function parseSettings(raw: unknown): Settings | null {
  let v: unknown = raw;
  if (typeof raw === "string") {
    try {
      v = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== "object") return null;
  const s = v as Record<string, unknown>;
  return {
    typeProfile: parseToggle(s["typeProfile"]),
    llvmPgo: parseToggle(s["llvmPgo"]),
    textOrder: parseToggle(s["textOrder"]),
    ...(typeof s["runsInstrumented"] === "boolean"
      ? { runsInstrumented: s["runsInstrumented"] }
      : {}),
    ...(typeof s["orderOnly"] === "boolean"
      ? { orderOnly: s["orderOnly"] }
      : {}),
  };
}

const OFF_RE = /^(off|disabled)\b/i;

/** "off (operator)" when a toggle switched the step off, else null. */
function offLabel(
  kind: "typeProfile" | "instrumented" | "llvmPgo" | "textOrder",
  rows: Array<StepRecord | undefined>,
  settings: Settings | null,
): string | null {
  if (kind === "instrumented") {
    if (settings?.runsInstrumented === false) {
      const op =
        settings.llvmPgo?.label.includes("operator") ||
        settings.textOrder?.label.includes("operator");
      return op ? "off (operator)" : "off (default)";
    }
  } else if (settings?.[kind] && settings[kind]!.on === false) {
    return settings[kind]!.label;
  }
  const present = rows.filter((r): r is StepRecord => !!r);
  if (present.length && present.every((r) => r.state === "skipped")) {
    const reason = present.find((r) => r.reason)?.reason ?? "";
    if (OFF_RE.test(reason)) return reason.split(/[;\n]/)[0]!.trim();
    if (/not enabled for this package/i.test(reason)) return "off (default)";
  }
  return null;
}

// ── helpers ───────────────────────────────────────────────────────────────
function worst(states: readonly StepState[]): StepState {
  if (states.length === 0) return "pending";
  if (states.includes("failed")) return "failed";
  if (states.includes("degraded")) return "degraded";
  if (states.includes("running")) return "running";
  if (states.every((s) => s === "skipped")) return "skipped";
  if (states.includes("pending")) return "pending";
  return "ok";
}

/** The state of a group of INDEPENDENT components (the benchmark pages):
 *  any failure / degradation shows, then anything running; all settled =
 *  ok (or skipped when nothing ran); some settled and some still pending =
 *  running (partially published - e.g. the Mac settles hours after the
 *  Linux components). */
export function componentsState(states: readonly StepState[]): StepState {
  if (states.includes("failed")) return "failed";
  if (states.includes("degraded")) return "degraded";
  if (states.includes("running")) return "running";
  if (states.every((s) => s === "skipped")) return "skipped";
  const open = states.some((s) => s === "pending");
  if (!open) return "ok";
  return states.includes("ok") ? "running" : "pending";
}

function buildState(b: StepBuild): StepState {
  switch (b.status) {
    case "running":
      return "running";
    case "queued":
      return "pending";
    case "succeeded":
      return "ok";
    case "failed":
    case "cancelled":
      return "failed";
    default:
      return "pending";
  }
}

const buildRef = (b: StepBuild): BuildRef => ({
  label: b.label,
  jobId: b.jobId ?? null,
  seqText: b.seq ? `#${b.seq}` : null,
  local: !!b.local,
  status: b.status,
});

function span(builds: readonly StepBuild[]): {
  start: number | null;
  end: number | null;
} {
  const starts = builds.flatMap((b) => (b.startedAt ? [b.startedAt] : []));
  if (starts.length === 0) return { start: null, end: null };
  const open = builds.some((b) => b.endedAt === undefined);
  const ends = builds.flatMap((b) => (b.endedAt ? [b.endedAt] : []));
  return {
    start: Math.min(...starts),
    end: open || ends.length === 0 ? null : Math.max(...ends),
  };
}

function blank(
  n: string,
  key: string,
  title: string,
  what: string,
  kind: StepKind,
  where: string,
  badges: string[],
): PublishStepRow {
  return {
    n,
    key,
    title,
    what,
    kind,
    where,
    badges,
    state: "pending",
    stateLabel: STATE_LABEL.pending,
    off: null,
    startedAt: null,
    endedAt: null,
    durationMs: null,
    runnerBuilds: 0,
    builds: [],
    subrows: [],
    reason: null,
    details: [],
    views: [],
    progress: null,
    linkPath: null,
  };
}

/** Sets state (+ the off label) and derives the duration. */
function settle(
  row: PublishStepRow,
  state: StepState,
  reason: string | null,
  off: string | null = null,
): PublishStepRow {
  row.state = off ? "skipped" : state;
  row.off = off;
  row.stateLabel = off ?? STATE_LABEL[row.state];
  row.reason = off ? (reason && reason !== off ? reason : null) : reason;
  if (row.startedAt !== null && row.endedAt !== null)
    row.durationMs = Math.max(0, row.endedAt - row.startedAt);
  row.runnerBuilds = row.builds.filter((b) => !b.local).length;
  return row;
}

function fmtTs(ms: number): string {
  return new Date(ms).toISOString().replace("T", " ").slice(0, 19) + "Z";
}

/** A runner-build row the checklist calls "running" is only running when one
 *  of its compile jobs is executing on a runner. The checklist marks the
 *  "addon-build" row running for the whole addon phase (collection,
 *  training, instrumented build ... final), so without this the "Final addon
 *  build" read "running" 16 minutes before its job was even submitted
 *  (prod 2026-10-15: it showed running next to the still-running type-profile
 *  collection build). No job yet = waiting for the earlier steps; jobs that
 *  are all still queued at the compile API = queued. */
function waitingState(row: PublishStepRow, waitingFor: string): void {
  if (row.kind !== "runner-build" || row.state !== "running") return;
  const jobs = row.builds.filter((b) => !b.local);
  if (jobs.some((b) => b.status === "running")) return;
  if (jobs.length === 0) {
    row.state = "pending";
    row.stateLabel = STATE_LABEL.pending;
    row.startedAt = null;
    row.endedAt = null;
    row.durationMs = null;
    row.reason = waitingFor;
    return;
  }
  if (jobs.every((b) => b.status === "queued")) {
    row.state = "pending";
    row.stateLabel = "queued";
    row.durationMs = null;
    row.reason = "submitted to the compile API; no runner has started it yet";
  }
}

/** The stored checklist of a build that is being re-benchmarked on the Mac
 *  (status `rebenching_macos`): only the `benchmark-mac` row is in flight.
 *  Every other row was settled before the re-run; the publisher refuses to
 *  start one while the darwin build, the Mac verify, the fixture or the
 *  glibc/musl sections are unfinished. A stored snapshot that still says
 *  "running" for such a row (a late settle that was never patched in) is
 *  therefore stale: it is shown as ok with that note instead of resurrecting
 *  a phantom running step with a ticking clock. */
function settledForRebench(steps: StepRecord[]): StepRecord[] {
  const out: StepRecord[] = steps.map((s) => {
    if (s.id === "benchmark-mac" || s.state !== "running") return s;
    const { progress: _progress, ...rest } = s;
    return {
      ...rest,
      state: "ok" as const,
      reason:
        s.reason ??
        "settled before the macOS re-run (final state not recorded in this checklist)",
      ...(s.endedAt === undefined && s.startedAt !== undefined
        ? { endedAt: s.startedAt }
        : {}),
    };
  });
  return out;
}

// ── the view ──────────────────────────────────────────────────────────────

/** Builds the 13-step view; null when the attempt has no step detail. */
export function publishStepsView(
  input: AttemptInput,
  nowMs: number = Date.now(),
): PublishStepsView | null {
  const parsed = parseSteps(input.steps);
  if (!parsed) return null;
  const rebench = input.status === "rebenching_macos";
  const raw = rebench
    ? settledForRebench(
        withDarwinFromTargets(parsed, input.nativeTargets, input.status),
      )
    : withDarwinFromTargets(parsed, input.nativeTargets, input.status);
  const by = new Map(raw.map((s) => [s.id, s] as const));
  const R = (id: string) => by.get(id);
  const settings = parseSettings(input.settings);
  // A macOS re-run (`rebenching_macos`) is NOT an in-flight pipeline: the
  // build was published long ago and every step but 12d is settled (the
  // publisher refuses a re-run otherwise), so unrecorded rows read as
  // "not recorded", never as pending/running work.
  const terminal = TERMINAL.has(input.status) || rebench;
  const attemptFailed =
    input.status === "failed" || input.status === "cancelled";

  /** pending in a finished attempt means "never reached". */
  const norm = (
    state: StepState | undefined,
    reason: string | undefined,
    why: string,
  ): [StepState, string | null] => {
    if (!state)
      return [terminal ? "skipped" : "pending", terminal ? why : null];
    if (state === "pending" && attemptFailed) return ["skipped", why];
    return [state, reason ?? null];
  };

  const rows: PublishStepRow[] = [];

  // 1 ── combined test build (runner VM) ────────────────────────────────
  const pt = R("package-test");
  const fp = R("full-project");
  const tb = R("test-build");
  // The combined "test-build" compile (internal/publish steps/
  // combinedTestBuild.ts) is listed by BOTH the package-test and the
  // full-project row with the same seq and job id: show it once.
  const testBuilds: StepBuild[] = tb?.builds
    ? tb.builds
    : [...(pt?.builds ?? []), ...(fp?.builds ?? [])].filter(
        (b, i, all) => all.findIndex((o) => o.seq === b.seq) === i,
      );
  const s1 = blank(
    "1",
    "test-build",
    "Combined test build",
    "standalone linux-x64 build of the package tests and the real-project suite (one compile of both, selected by argv; two compile jobs when PUBLISH_COMBINED_TESTBUILD=0)",
    "runner-build",
    "runner VM (Firecracker)",
    ["musl", "linux-x64"],
  );
  s1.builds = [];
  s1.subrows = testBuilds.map((b, i) => {
    const sub = blank(
      `1.${i + 1}`,
      `test-build-${i + 1}`,
      b.label,
      "compile job",
      "runner-build",
      "runner VM (Firecracker)",
      ["musl", "linux-x64"],
    );
    sub.builds = [buildRef(b)];
    sub.startedAt = b.startedAt || null;
    sub.endedAt = b.endedAt ?? null;
    return settle(sub, buildState(b), null);
  });
  const sp1 = span(testBuilds);
  s1.startedAt = sp1.start ?? tb?.startedAt ?? pt?.startedAt ?? null;
  s1.endedAt = sp1.end;
  s1.runnerBuilds = 0;
  const compileFailed =
    testBuilds.some((b) => buildState(b) === "failed") ||
    // old data: a failed row that never reached its run
    (pt?.state === "failed" &&
      pt.runStartedAt === undefined &&
      !pt.builds?.length);
  let s1State: StepState;
  let s1Reason: string | null = null;
  if (tb) {
    [s1State, s1Reason] = norm(tb.state, tb.reason, "not reached");
  } else if (testBuilds.length > 0) {
    const bs = worst(testBuilds.map(buildState));
    s1State = bs;
    if (bs === "failed")
      s1Reason =
        (pt?.state === "failed" && pt.runStartedAt === undefined
          ? pt.reason
          : undefined) ??
        (fp?.state === "failed" && fp.runStartedAt === undefined
          ? fp.reason
          : undefined) ??
        null;
  } else {
    [s1State, s1Reason] = norm(pt?.state, pt?.reason, "not reached");
  }
  settle(s1, s1State, s1Reason);
  const s1Retries = [pt, fp, tb].find((r) => r?.facts?.["infraRetries"]);
  if (s1Retries)
    s1.details.push({
      label: "Infra retries",
      value: String(s1Retries.facts?.["infraRetries"]),
    });
  s1.runnerBuilds = testBuilds.filter((b) => !b.local).length;
  rows.push(s1);

  // 2, 3 ── accuracy runs against the node oracle (publish host) ─────────
  const runStep = (
    n: string,
    key: string,
    title: string,
    what: string,
    raw: StepRecord | undefined,
    builds: StepBuild[],
    skippedWhy: string,
  ): PublishStepRow => {
    const r = blank(
      n,
      key,
      title,
      what,
      "local-run",
      "publish host (bwrap sandbox)",
      ["linux-x64 musl binary"],
    );
    const lastBuildEnd = builds.reduce<number | null>(
      (m, b) => (b.endedAt && (m === null || b.endedAt > m) ? b.endedAt : m),
      null,
    );
    r.startedAt = raw?.runStartedAt ?? lastBuildEnd ?? null;
    if (
      s1.state === "failed" ||
      (raw && builds.some((b) => buildState(b) === "failed"))
    ) {
      return settle(
        r,
        terminal ? "skipped" : "pending",
        "not reached: the test build failed",
      );
    }
    if (!raw)
      return settle(
        r,
        terminal ? "skipped" : "pending",
        terminal ? skippedWhy : null,
      );
    if (raw.state === "skipped")
      return settle(r, "skipped", raw.reason ?? skippedWhy);
    if (raw.state === "pending") {
      const [st, why] = norm("pending", undefined, "not reached");
      return settle(r, st, why);
    }
    if (raw.state === "running") {
      // still compiling: the run has not begun
      if (
        raw.runStartedAt === undefined &&
        builds.some((b) => buildState(b) === "running")
      ) {
        r.startedAt = null;
        return settle(r, "pending", null);
      }
      return settle(r, "running", null);
    }
    r.endedAt = raw.endedAt ?? null;
    return settle(r, raw.state, raw.reason ?? null);
  };
  rows.push(
    runStep(
      "2",
      "package-test-run",
      "Package-test run",
      "accuracy: the compiled test output vs the real node oracle",
      pt,
      pt?.builds ?? [],
      "not run in this attempt",
    ),
  );
  rows.push(
    runStep(
      "3",
      "full-project-run",
      "Full-project run",
      "accuracy: the package's real upstream test suite vs the real node oracle",
      fp,
      fp?.builds ?? [],
      "no full-project test for this package",
    ),
  );

  // helper for builds-backed optimisation steps
  const optStep = (
    n: string,
    key: string,
    title: string,
    what: string,
    kind: StepKind,
    where: string,
    badges: string[],
    rawRows: Array<StepRecord | undefined>,
    off: string | null,
    notReached: string,
  ): PublishStepRow => {
    const row = blank(n, key, title, what, kind, where, badges);
    const main = rawRows[0];
    const bs = rawRows.flatMap((r) => r?.builds ?? []);
    row.builds = bs.map(buildRef);
    const sp = span(bs);
    row.startedAt = sp.start ?? main?.startedAt ?? null;
    row.endedAt = sp.end ?? (bs.length ? null : (main?.endedAt ?? null));
    const states = rawRows.flatMap((r) => (r ? [r.state] : []));
    if (off) return settle(row, "skipped", main?.reason ?? null, off);
    const [st, why] = norm(
      states.length ? worst(states) : undefined,
      rawRows.find((r) => r?.state === "degraded" || r?.state === "failed")
        ?.reason ?? rawRows.find((r) => r?.reason)?.reason,
      notReached,
    );
    if (st === "skipped" && row.endedAt === null && row.startedAt !== null)
      row.endedAt = row.startedAt;
    // Automatic resubmissions after a runner-side infra failure
    // (publish infraRetry.ts: "retried after heartbeat_lost (1/2)").
    const retried = rawRows.find((r) => r?.facts?.["infraRetries"]);
    if (retried)
      row.details.push({
        label: "Infra retries",
        value: String(retried.facts?.["infraRetries"]),
      });
    return settle(row, st, why);
  };

  // 4, 5 ── type profile ──────────────────────────────────────────────────
  const tp = R("type-profile");
  const tpt = R("type-profile-training");
  const tpOff = offLabel("typeProfile", [tp, tpt], settings);
  const s4 = optStep(
    "4",
    "type-profile-build",
    "Type-profile collection build",
    "instrumented addon build whose run records the types the package sees",
    "runner-build",
    "runner VM (Firecracker)",
    ["musl", "linux-x64", "addon"],
    [tp],
    tpOff,
    "not reached",
  );
  waitingState(s4, "waiting for an earlier step");
  rows.push(s4);
  rows.push(
    optStep(
      "5",
      "type-profile-training",
      "Type-profile training run",
      "the instrumented addon runs the package-test and full-project workloads",
      "local-run",
      "publish host (local run)",
      ["glibc"],
      [tpt],
      tpOff,
      "not reached",
    ),
  );

  // 6, 7 ── LLVM PGO ──────────────────────────────────────────────────────
  const li = R("llvm-instrumented");
  const lt = R("llvm-training");
  const to = R("text-order");
  const instrOff = offLabel("instrumented", [li, lt], settings);
  const s6 = optStep(
    "6",
    "llvm-instrumented-build",
    "LLVM instrumented build",
    "addon built with LLVM profile generation (package crates only)",
    "runner-build",
    "runner VM (Firecracker)",
    ["musl", "linux-x64", "addon"],
    [li],
    instrOff,
    "not reached",
  );
  waitingState(s6, "waiting for the type-profile steps (4-5) to finish");
  rows.push(s6);
  const s7 = optStep(
    "7",
    "llvm-training",
    "LLVM training run",
    "the instrumented addon runs the training workloads; the text order is derived from its profile",
    "local-run",
    "publish host (local run)",
    ["glibc"],
    [lt],
    instrOff,
    "not reached",
  );
  const f7 = lt?.facts;
  if (f7) {
    if (f7["textOrderSource"] !== undefined)
      s7.details.push({
        label: "Text order",
        value: `${f7["textOrderSource"]}${f7["textOrderEntries"] !== undefined ? `, ${f7["textOrderEntries"]} entries` : ""}${f7["textOrderSha256"] ? `, sha256 ${String(f7["textOrderSha256"]).slice(0, 16)}` : ""}`,
      });
  }
  if (to) {
    const toOff = offLabel("textOrder", [to], settings);
    s7.details.push({
      label: "Text order step",
      value: toOff ?? `${to.state}${to.reason ? ` — ${to.reason}` : ""}`,
    });
    if (!toOff && to.state === "degraded" && s7.state === "ok") {
      s7.state = "degraded";
      s7.stateLabel = STATE_LABEL.degraded;
      s7.reason = to.reason ?? "text order degraded";
    }
  }
  rows.push(s7);

  // 8 ── final addon build (runner VM) ─────────────────────────────────
  const ab = R("addon-build");
  const s8 = optStep(
    "8",
    "final-addon-build",
    "Final addon build",
    "the shipped linux-x64 musl addon, with the optimisations below",
    "runner-build",
    "runner VM (Firecracker)",
    ["musl", "linux-x64", "addon"],
    [ab],
    null,
    "not reached",
  );
  waitingState(
    s8,
    "waiting for the profile steps (4-7) to finish: the final build has not been submitted yet",
  );
  const applied = (
    name: string,
    rr: Array<StepRecord | undefined>,
    off: string | null,
  ): DetailLine => {
    if (off) return { label: name, value: off };
    const present = rr.filter((r): r is StepRecord => !!r);
    const st = present.length ? worst(present.map((r) => r.state)) : "pending";
    const why = present.find(
      (r) => r.state === "degraded" || r.state === "failed",
    )?.reason;
    const note = present.find((r) => r.reason)?.reason;
    switch (st) {
      case "ok":
        return { label: name, value: "applied" };
      case "degraded":
      case "failed":
        return {
          label: name,
          value: `degraded — ${why ?? "see the step above"}`,
        };
      case "skipped":
        return { label: name, value: `not applied${note ? ` — ${note}` : ""}` };
      default:
        return { label: name, value: st === "running" ? "running" : "pending" };
    }
  };
  // "applied" only when the final compile's build record confirmed the
  // compiler consumed the profile (the type-profile-applied row); an attempt
  // stored before that row existed falls back to the collection rows.
  const tpa = R("type-profile-applied");
  const tpLine = applied(
    "Type profile",
    tpa ? [tp, tpt, tpa] : [tp, tpt],
    tpOff,
  );
  if (tpLine.value === "applied" && tpa?.reason)
    tpLine.value = `applied — ${tpa.reason}`;
  s8.details.push(
    tpLine,
    applied(
      "LLVM PGO",
      [R("llvm-pgo") ?? lt],
      offLabel("llvmPgo", [R("llvm-pgo")], settings),
    ),
    applied("Text order", [to], offLabel("textOrder", [to], settings)),
  );
  rows.push(s8);

  // 9 ── addon verify (publish host, glibc) ─────────────────────────────
  rows.push(
    optStep(
      "9",
      "addon-verify",
      "Addon verify",
      "accuracy of the shipped addon vs the real node oracle (installed from the tarball)",
      "local-run",
      "publish host (bwrap sandbox)",
      ["glibc"],
      [R("addon-verify")],
      null,
      "not reached",
    ),
  );

  // 10 ── publish: the package to the registry ──────────────────────────
  // Everything after this runs independently of the others: the darwin
  // follow-up (11) and the four benchmark components (12a-12d) all start
  // from the published linux build.
  const pub = R("publish");
  const s10pub = blank(
    "10",
    "publish",
    "Publish",
    "the package to the registry",
    "publish",
    "publish host → registry.pure.dev",
    [],
  );
  const a = blank(
    "10a",
    "publish-package",
    "Package published to the registry",
    "immutable build + moving X.Y.Z pointer",
    "publish",
    "publish host → registry.pure.dev",
    [],
  );
  if (pub) {
    a.startedAt = pub.startedAt ?? null;
    a.endedAt =
      pub.state === "running" || pub.state === "pending"
        ? null
        : (pub.endedAt ?? null);
  }
  const pf = pub?.facts;
  const buildName =
    (pf?.["buildName"] as string | undefined) ?? input.publishedAs ?? undefined;
  if (buildName) a.details.push({ label: "Build", value: buildName });
  if (pf?.["integrity"])
    a.details.push({ label: "Integrity", value: String(pf["integrity"]) });
  if (pf?.["pointer"])
    a.details.push({ label: "X.Y.Z pointer", value: String(pf["pointer"]) });
  if (a.endedAt)
    a.details.push({ label: "Published at", value: fmtTs(a.endedAt) });
  if (input.status === "verified")
    settle(a, "skipped", "verify-only run: nothing is uploaded");
  else {
    const [st, why] = norm(pub?.state, pub?.reason, "not reached");
    settle(a, st, st === "ok" ? null : why);
  }
  s10pub.subrows = [a];
  s10pub.startedAt = a.startedAt;
  s10pub.endedAt = a.endedAt;
  settle(s10pub, a.state, null);
  s10pub.stateLabel = a.stateLabel;
  rows.push(s10pub);

  // 11 ── darwin follow-up (runner VM, decoupled) ───────────────────────
  const dw = R("darwin-followup");
  const s10 = optStep(
    "11",
    "darwin-followup",
    "darwin arm64 follow-up build",
    "decoupled: starts after the linux build is published and adds the macOS platform package in a later build",
    "runner-build",
    "runner VM (Firecracker)",
    ["macos-arm64"],
    [dw],
    null,
    "not reached",
  );
  if (s10.state === "pending" && !terminal) s10.reason = null;
  // A darwin record on the row before THIS attempt published is the previous
  // attempt's: say so, and show none of its job / telemetry as this run's.
  const prevDarwin = darwinRecordFor(
    input.nativeTargets,
    input.status,
  ).previous;
  if (prevDarwin)
    s10.details.push({
      label: "Previous attempt",
      value:
        prevDarwin.status === "shipped"
          ? `reused, not rebuilt by this attempt: darwin arm64 shipped in ${prevDarwin.publishedIn ?? "an earlier build"}${prevDarwin.jobId ? ` (job ${prevDarwin.jobId}, from a previous run)` : ""}; this attempt's own darwin build starts after its publish`
          : `an earlier attempt's darwin record (${prevDarwin.status}${prevDarwin.reason ? `: ${prevDarwin.reason}` : ""}); this attempt's own darwin build starts after its publish`,
    });
  // 11a ── hardware verification on the Mac (macos-tester), evidence only: an
  // unavailable Mac shows as degraded WITH its reason and never blocks. Shown
  // only when the publisher sent the row (older runs have none).
  const mv = R("macos-verify");
  if (mv) {
    const s10a = optStep(
      "11a",
      "macos-verify",
      "macOS hardware verify",
      "the darwin addon loaded and run on a real Mac (macos-tester) and diffed against the node oracle",
      "local-run",
      "macOS host (macos-tester)",
      ["macos-arm64"],
      [mv],
      null,
      "not reached",
    );
    if (s10a.state === "pending" && !terminal) s10a.reason = null;
    s10.subrows = [s10a];
  }
  rows.push(s10);

  // 11, 12 ── benchmark: glibc, musl ───────────────────────────────────
  const bn = R("benchmark");
  const bg = R("benchmark-glibc");
  const bm = R("benchmark-musl");
  const mp = R("musl-preflight");
  const gp = R("glibc-preflight");
  const prog = bn?.state === "running" ? bn.progress : undefined;
  const progMusl = !!prog && /musl/i.test(prog.view);
  const progText = (p: NonNullable<typeof prog>): string => {
    const v = Math.min(p.viewsDone + 1, p.viewsTotal);
    return `${p.view} · view ${v}/${p.viewsTotal}${
      p.workloadsTotal > 0
        ? ` · workload ${Math.min(p.workloadsDone + 1, p.workloadsTotal)}/${p.workloadsTotal}${p.workload ? ` (${p.workload})` : ""}`
        : ""
    }`;
  };
  const viewsOf = (libc: string): ViewLine[] =>
    (bn?.views ?? [])
      .filter((v) => v.libc === libc)
      .map((v) => ({
        title: `${v.runtime} · ${libc === "musl" ? "Alpine musl" : "glibc"}`,
        state: v.state,
        reason: v.reason ?? null,
        headline: v.headline ?? null,
      }));
  const benchRow = (
    n: string,
    key: string,
    title: string,
    libc: "glibc" | "musl",
    raw: StepRecord | undefined,
    pre: StepRecord | undefined,
  ): PublishStepRow => {
    const row = blank(
      n,
      key,
      title,
      libc === "glibc"
        ? "node 26 / 24 / 22 and bun: node + npm vs node + Pure addon, per workload"
        : "the same matrix in the Alpine container",
      "benchmark",
      libc === "glibc"
        ? "publish host · glibc container"
        : "Alpine container on the publish host",
      [libc],
    );
    row.views = viewsOf(libc);
    row.startedAt = bn?.startedAt ?? null;
    if (pre && pre.state !== "ok" && pre.reason)
      row.details.push({ label: "Environment", value: pre.reason });
    if (libc === "glibc") {
      const f = bn?.facts;
      if (f?.["headline"])
        row.details.push({ label: "Headline", value: String(f["headline"]) });
    }
    // The component's own row (publisher 2026-10-16+): it settles when ITS
    // variant arrives, independently of the other components and of the
    // aggregate `benchmark` row.
    if (raw && raw.state !== "pending" && raw.state !== "running") {
      row.startedAt = raw.startedAt ?? bn?.startedAt ?? null;
      row.endedAt = raw.endedAt ?? null;
      if (raw.state === "ok" && raw.reason)
        row.details.push({ label: "Measured", value: raw.reason });
      return settle(
        row,
        raw.state,
        raw.state === "ok" ? null : (raw.reason ?? null),
      );
    }
    if (!bn)
      return settle(
        row,
        terminal ? "skipped" : "pending",
        terminal ? "not reached" : null,
      );
    if (bn.state === "skipped")
      return settle(
        row,
        "skipped",
        bn.reason ?? raw?.reason ?? "benchmarks disabled",
      );
    if (bn.state === "pending")
      return settle(
        row,
        attemptFailed ? "skipped" : "pending",
        attemptFailed ? "not reached" : null,
      );
    if (bn.state === "running") {
      const isMuslNow = progMusl;
      if (libc === "glibc") {
        if (isMuslNow) {
          row.endedAt = null;
          return settle(row, "ok", null);
        }
        if (prog) row.progress = progText(prog);
        return settle(row, "running", null);
      }
      if (!isMuslNow) return settle(row, "pending", null);
      if (prog) row.progress = progText(prog);
      return settle(row, "running", null);
    }
    // the benchmark finished
    row.endedAt =
      libc === "musl" || !(bn.views ?? []).some((v) => v.libc === "musl")
        ? (bn.endedAt ?? null)
        : null;
    const vs = row.views;
    const rawReasons = vs.flatMap((v) =>
      v.reason ? [`${v.title}: ${v.reason}`] : [],
    );
    let st: StepState;
    if (raw && raw.state !== "pending") st = raw.state;
    else if (vs.length > 0)
      st = vs.every((v) => v.state === "unavailable")
        ? "failed"
        : vs.some((v) => v.state !== "ok")
          ? "degraded"
          : "ok";
    else st = bn.state === "ok" ? "ok" : bn.state;
    const reason =
      raw?.reason ??
      (rawReasons.length
        ? rawReasons.join("\n")
        : libc === "glibc"
          ? (bn.reason ?? null)
          : null);
    return settle(row, st, st === "ok" ? null : reason);
  };
  // 12 ── benchmark pages: FOUR independently published components ─────
  // registry.pure.dev/-/stats/<package> gains one part per component the
  // moment that component settles (steps/benchRuns.ts): the fixture first
  // (before anything is measured), glibc and musl as each finishes, and macOS
  // whenever the Mac settles - after the darwin build (11), hours later. None
  // waits for another; one failing never blocks or downgrades the rest.
  const s12 = blank(
    "12",
    "benchmark-pages",
    "Benchmark pages",
    "registry.pure.dev/-/stats/<package>: each component is published as its own part of the page when it settles, independently of the others",
    "benchmark",
    "publish host → broker → registry.pure.dev",
    [],
  );
  s12.linkPath = `/-/stats/${input.packageName}`;
  const benchOff =
    input.status === "verified" || bn?.state === "skipped"
      ? (bn?.reason ?? "no benchmark in this run")
      : null;

  // 12a ── fixtures (content-addressed, published first) ────────────────
  const fxRaw = R("benchmark-fixtures");
  const s12a = blank(
    "12a",
    "benchmark-fixtures",
    "Fixtures published",
    "the content-addressed test fixture (workload tree + generated tier files) the Mac runs, uploaded to registry.pure.dev before anything is measured",
    "publish",
    "publish host → registry.pure.dev",
    [],
  );
  s12a.startedAt = fxRaw?.startedAt ?? bn?.startedAt ?? null;
  if (fxRaw?.facts?.["sha256"])
    s12a.details.push({
      label: "Fixture sha256",
      value: String(fxRaw.facts["sha256"]),
    });
  if (fxRaw?.facts?.["bytes"] !== undefined)
    s12a.details.push({
      label: "Fixture bytes",
      value: String(fxRaw.facts["bytes"]),
    });
  if (fxRaw && fxRaw.state !== "pending" && fxRaw.state !== "running") {
    s12a.endedAt = fxRaw.endedAt ?? null;
    settle(
      s12a,
      fxRaw.state,
      fxRaw.state === "ok" ? null : (fxRaw.reason ?? null),
    );
  } else if (benchOff) settle(s12a, "skipped", benchOff);
  else if (!fxRaw)
    // a publisher from before the per-component rows
    settle(
      s12a,
      terminal ? "skipped" : "pending",
      terminal ? "not recorded by this publisher version" : null,
    );
  else if (attemptFailed) settle(s12a, "skipped", "not reached");
  else if (fxRaw.state === "running") settle(s12a, "running", null);
  else
    settle(
      s12a,
      "pending",
      fxRaw.reason ?? (terminal ? "waiting for the benchmark to start" : null),
    );

  // 12b, 12c ── glibc, musl ─────────────────────────────────────────────
  const s12b = benchRow(
    "12b",
    "benchmark-glibc",
    "Benchmark — glibc",
    "glibc",
    bg,
    gp,
  );
  const s12c = benchRow(
    "12c",
    "benchmark-musl",
    "Benchmark — musl (Alpine)",
    "musl",
    bm,
    mp,
  );

  // 12d ── macOS: settles after the darwin build (11), on the Mac's clock ──
  const mbRaw = R("benchmark-mac");
  const macBench = darwinRecordFor(input.nativeTargets, input.status).current
    ?.macBench;
  const s12d = blank(
    "12d",
    "benchmark-mac",
    "Benchmark — macOS (arm64)",
    "the same workloads on a real Mac with the darwin-arm64 addon; starts once the darwin build (11) is published and the fixture (12a) is up",
    "benchmark",
    "macOS host (macos-tester)",
    ["macos-arm64"],
  );
  s12d.startedAt = mbRaw?.startedAt ?? null;
  if (mbRaw?.state === "running") {
    // An operator "Re-run macOS benchmark" (publish steps/macRebench.ts)
    // marks this row running while the Mac measures; the darwin record's
    // macBench still holds the PREVIOUS verdict and must not hide it.
    settle(s12d, "running", null);
    if (mbRaw.progress) s12d.progress = progText(mbRaw.progress);
  } else if (mbRaw && mbRaw.state !== "pending") {
    s12d.endedAt = mbRaw.endedAt ?? null;
    settle(
      s12d,
      mbRaw.state,
      mbRaw.state === "ok" ? null : (mbRaw.reason ?? null),
    );
  } else if (macBench && macBench.state) {
    // The darwin record carries the Mac's verdict (also for publishers
    // that do not send the component row).
    s12d.endedAt = macBench.measuredAt ?? null;
    const ms = macBench.state;
    if (ms === "ok") settle(s12d, "ok", null);
    else
      settle(
        s12d,
        "degraded",
        ms === "noisy"
          ? "noisy: published with a visible badge"
          : ms === "invalid"
            ? `results not trusted${macBench.reason ? `: ${macBench.reason}` : ""}`
            : `macOS UNAVAILABLE (${macBench.reason ?? "no Mac numbers"})`,
      );
  } else if (benchOff) settle(s12d, "skipped", benchOff);
  else if (attemptFailed) settle(s12d, "skipped", "not reached");
  else if (terminal && (s10.state === "failed" || s10.state === "skipped"))
    settle(
      s12d,
      "skipped",
      `no darwin-arm64 build to measure${s10.reason ? `: ${s10.reason}` : ""}`,
    );
  else if (!mbRaw && terminal && !s10.builds.length)
    settle(s12d, "skipped", "not recorded by this publisher version");
  else
    settle(
      s12d,
      "pending",
      mbRaw?.reason ?? "waits for the darwin-arm64 build (11) and a free Mac",
    );

  s12.subrows = [s12a, s12b, s12c, s12d];
  const kids = s12.subrows.map((k) => k.state);
  const settledKids = kids.filter((k) => k !== "pending" && k !== "running");
  s12.startedAt = bn?.startedAt ?? s12a.startedAt ?? null;
  const kidEnds = s12.subrows.flatMap((k) =>
    k.endedAt !== null ? [k.endedAt] : [],
  );
  s12.endedAt =
    settledKids.length === kids.length && kidEnds.length > 0
      ? Math.max(...kidEnds)
      : null;
  const bf = bn?.facts;
  if (bf?.["schema"])
    s12.details.push({ label: "Schema", value: String(bf["schema"]) });
  if (bf?.["benchStatus"])
    s12.details.push({
      label: "Benchmark result",
      value: String(bf["benchStatus"]),
    });
  if (bf?.["headline"])
    s12.details.push({ label: "Headline", value: String(bf["headline"]) });
  settle(s12, componentsState(kids), null);
  rows.push(s12);

  // ── summary ──────────────────────────────────────────────────────────
  const flat = rows.flatMap((r) => [r, ...r.subrows]);
  const starts = flat.flatMap((r) =>
    r.startedAt !== null ? [r.startedAt] : [],
  );
  const ends = flat.flatMap((r) => (r.endedAt !== null ? [r.endedAt] : []));
  const running = flat.some(
    // The darwin follow-up outlives the run, and the benchmark-pages parent
    // reads "running" while only the (hours-later) Mac component is open:
    // its own children decide whether the attempt's clock still ticks.
    (r) =>
      r.state === "running" &&
      r.key !== "darwin-followup" &&
      r.key !== "benchmark-pages",
  );
  const elapsedMs =
    starts.length === 0
      ? null
      : Math.max(
          0,
          (running || ends.length === 0 ? nowMs : Math.max(...ends)) -
            Math.min(...starts),
        );
  const runnerBuilds = rows.reduce((n, r) => n + r.runnerBuilds, 0);
  const localRuns = rows.filter(
    (r) =>
      r.kind === "local-run" &&
      (r.state === "running" ||
        r.state === "ok" ||
        r.state === "degraded" ||
        r.state === "failed"),
  ).length;
  return {
    rows,
    elapsedMs,
    runnerBuilds,
    localRuns,
    summary: `${runnerBuilds} runner build${runnerBuilds === 1 ? "" : "s"}, ${localRuns} local run${localRuns === 1 ? "" : "s"}`,
    terminal,
  };
}

export interface ChronoEvent {
  n: string;
  title: string;
  startedAt: number;
  endedAt: number | null;
}

/** The true order things happened in (rows with a start time, by start). */
export function chronology(view: PublishStepsView): ChronoEvent[] {
  return view.rows
    .flatMap((r) => (r.subrows.length ? [r, ...r.subrows] : [r]))
    .flatMap((r) =>
      r.startedAt !== null
        ? [
            {
              n: r.n,
              title: r.title,
              startedAt: r.startedAt,
              endedAt: r.endedAt,
            },
          ]
        : [],
    )
    .sort((x, y) => x.startedAt - y.startedAt);
}

/** "2m 03s" style duration, "—" when unknown. */
export function fmtMs(ms: number | null): string {
  if (ms === null) return "—";
  const t = Math.round(ms / 1000);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

/** "14:03:22" in the viewer's zone, "—" when unknown. */
export function fmtClock(ms: number | null): string {
  if (ms === null) return "—";
  const d = new Date(ms);
  const p = (x: number) => String(x).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
